/**
 * Sub-spec 09 Plan 8 — /settings/integrations/gbp
 *
 * Shows the connected GBP account, a location picker (populated from
 * GET /api/integrations/gbp/locations), the review link URL, and a toggle
 * for "Auto-send review request 24h after checkout".
 *
 * When not connected, links to the connect flow via /api/integrations/gbp/start.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface SyncConfig {
  connected: boolean;
  locationName?: string | null;
  reviewLinkUrl?: string | null;
  autoSendReviewRequest?: boolean;
  externalAccountLabel?: string | null;
}

interface LocationRow {
  accountName: string;
  locationName: string;
  title: string;
}

export function GbpSettings() {
  const [config, setConfig] = useState<SyncConfig | null>(null);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [reviewLink, setReviewLink] = useState('');
  const [autoSend, setAutoSend] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/integrations/gbp/sync-config', { credentials: 'include' });
      if (!res.ok) {
        setError('Unable to load GBP config.');
        return;
      }
      const body = (await res.json()) as SyncConfig;
      setConfig(body);
      if (body.connected) {
        setSelectedLocation(body.locationName ?? '');
        setReviewLink(body.reviewLinkUrl ?? '');
        setAutoSend(body.autoSendReviewRequest ?? true);
        // Best-effort location list.
        try {
          const locRes = await fetch('/api/integrations/gbp/locations', {
            credentials: 'include',
          });
          if (locRes.ok) {
            const locBody = (await locRes.json()) as { locations: LocationRow[] };
            setLocations(locBody.locations ?? []);
          }
        } catch {
          /* non-fatal */
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const connect = useCallback(async () => {
    const res = await fetch('/api/integrations/gbp/start', { credentials: 'include' });
    if (!res.ok) {
      setError('Could not start connection.');
      return;
    }
    const body = (await res.json()) as { authUrl: string };
    window.location.assign(body.authUrl);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/integrations/gbp/sync-config', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          locationName: selectedLocation || null,
          reviewLinkUrl: reviewLink || null,
          autoSendReviewRequest: autoSend,
        }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string; issues?: string[] };
        setError(err?.issues?.join(', ') ?? err?.error ?? 'Save failed.');
        return;
      }
      await load();
    } finally {
      setSaving(false);
    }
  }, [selectedLocation, reviewLink, autoSend, load]);

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <h1 className="text-xl font-semibold">Google Business Profile</h1>
        <p className="text-sm text-muted-foreground">
          Send a review request email to each customer 24 hours after they check out. The link
          deep-links into your Google review surface.
        </p>
      </header>

      {error ? (
        <div className="rounded-md bg-warning/10 border border-warning px-3 py-2 text-sm text-warning">
          {error}
        </div>
      ) : null}

      {!config?.connected ? (
        <div className="space-y-2">
          <p className="text-sm">Not connected.</p>
          <button
            type="button"
            className="text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5"
            onClick={() => void connect()}
          >
            Connect Google Business Profile
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {config.externalAccountLabel ? (
            <p className="text-xs text-muted-foreground">
              Connected as <span className="font-medium">{config.externalAccountLabel}</span>
            </p>
          ) : null}

          <div>
            <label className="block text-sm font-medium mb-1">Location</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="text-sm rounded-md border border-border px-3 py-2 w-full"
            >
              <option value="">— Select a location —</option>
              {locations.map((l) => (
                <option key={l.locationName} value={l.locationName}>
                  {l.title}
                </option>
              ))}
              {selectedLocation && !locations.some((l) => l.locationName === selectedLocation) ? (
                <option value={selectedLocation}>{selectedLocation}</option>
              ) : null}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Review link URL</label>
            <input
              type="url"
              value={reviewLink}
              onChange={(e) => setReviewLink(e.target.value)}
              placeholder="https://search.google.com/local/writereview?placeid=..."
              className="text-sm rounded-md border border-border px-3 py-2 w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Customer emails use this URL. Find your place ID at{' '}
              <a
                href="https://developers.google.com/maps/documentation/places/web-service/place-id"
                className="underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google's Place ID Finder
              </a>
              .
            </p>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={autoSend}
              onChange={(e) => setAutoSend(e.target.checked)}
            />
            <span className="text-sm">Auto-send review request 24 hours after checkout</span>
          </label>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
              onClick={() => void save()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <Link to="/settings/integrations" className="text-sm underline">
              Back to integrations
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
